import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Users, CreditCard, FileText, ClipboardCheck, TrendingUp, 
  Search, UserCheck, UserX, LogOut, RefreshCw, CheckCircle,
  XCircle, Clock, DollarSign, Activity, Calendar, Mail, Send, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    to_emails: [],
    send_to_all: false
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || !stats) {
        const statsRes = await axios.get(`${API_URL}/api/admin/dashboard-stats`);
        setStats(statsRes.data);
      }
      
      if (activeTab === 'users') {
        const usersRes = await axios.get(`${API_URL}/api/admin/users-detailed`);
        setUsers(usersRes.data);
      }
      
      if (activeTab === 'requests') {
        const requestsRes = await axios.get(`${API_URL}/api/admin/subscription-requests`);
        setRequests(requestsRes.data);
      }
      
      if (activeTab === 'coupons') {
        const couponsRes = await axios.get(`${API_URL}/api/admin/coupons`);
        setCoupons(couponsRes.data);
      }
      
      if (activeTab === 'emails') {
        const logsRes = await axios.get(`${API_URL}/api/email/admin/email-logs`);
        setEmailLogs(logsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.message.trim()) {
      toast.error('Please enter subject and message');
      return;
    }
    if (!emailData.send_to_all && emailData.to_emails.length === 0) {
      toast.error('Please select recipients or check "Send to All"');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/admin/send-email`, emailData);
      toast.success(res.data.message);
      setShowEmailComposer(false);
      setEmailData({ subject: '', message: '', to_emails: [], send_to_all: false });
      if (activeTab === 'emails') fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEmailUser = (email) => {
    setEmailData(prev => ({ ...prev, to_emails: [email], send_to_all: false }));
    setShowEmailComposer(true);
  };

  const handleBlockUser = async (userId, block) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/${block ? 'block' : 'unblock'}`);
      toast.success(`User ${block ? 'blocked' : 'unblocked'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleActivateSubscription = async (userId, tier) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/activate-subscription?tier=${tier}`);
      toast.success('Subscription activated');
      fetchData();
    } catch (error) {
      toast.error('Activation failed');
    }
  };

  const handleDeactivateSubscription = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/deactivate-subscription`);
      toast.success('Subscription deactivated');
      fetchData();
    } catch (error) {
      toast.error('Deactivation failed');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.school_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'subscribers') return matchesSearch && user.subscription_status === 'active';
    if (filterStatus === 'trial') return matchesSearch && user.subscription_status === 'trial';
    if (filterStatus === 'blocked') return matchesSearch && user.is_blocked;
    return matchesSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Never') return 'Never';
    try {
      return new Date(dateStr).toLocaleDateString('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm">SINO TECHNOLOGY FOR SCHOOLS</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchData}
              variant="outline"
              className="text-slate-300 border-slate-600 hover:bg-slate-700"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-red-400 border-red-800 hover:bg-red-900/50"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'requests', label: 'Requests', icon: Clock },
            { id: 'emails', label: 'Emails', icon: Mail },
            { id: 'coupons', label: 'Coupons', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'requests' && requests.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {requests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={Users}
                    label="Total Users"
                    value={stats.total_users}
                    color="blue"
                  />
                  <StatCard
                    icon={UserCheck}
                    label="Active Subscribers"
                    value={stats.active_subscribers}
                    color="green"
                  />
                  <StatCard
                    icon={Clock}
                    label="Trial Users"
                    value={stats.trial_users}
                    color="amber"
                  />
                  <StatCard
                    icon={DollarSign}
                    label="Monthly Revenue"
                    value={`R${stats.monthly_revenue}`}
                    color="teal"
                  />
                </div>

                {/* Usage Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    icon={FileText}
                    label="Lesson Plans Generated"
                    value={stats.total_lessons}
                    color="purple"
                  />
                  <StatCard
                    icon={ClipboardCheck}
                    label="Quizzes Generated"
                    value={stats.total_quizzes}
                    color="pink"
                  />
                  <StatCard
                    icon={Activity}
                    label="Worksheets Generated"
                    value={stats.total_worksheets}
                    color="indigo"
                  />
                </div>

                {/* Subscription Breakdown */}
                <div className="bg-slate-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Subscription Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">R25 Plan</p>
                      <p className="text-2xl font-bold text-white">{stats.r25_subscribers}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">R50 Plan</p>
                      <p className="text-2xl font-bold text-white">{stats.r50_subscribers}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">Free/Trial</p>
                      <p className="text-2xl font-bold text-white">{stats.free_users}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">New This Week</p>
                      <p className="text-2xl font-bold text-green-400">+{stats.new_users_this_week}</p>
                    </div>
                  </div>
                </div>

                {/* Pending Requests Alert */}
                {stats.pending_requests > 0 && (
                  <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-6 h-6 text-amber-500" />
                      <div>
                        <p className="text-amber-200 font-medium">Pending Subscription Requests</p>
                        <p className="text-amber-400 text-sm">{stats.pending_requests} requests waiting for approval</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setActiveTab('requests')}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      View Requests
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by email, name, or school..."
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                        data-testid="search-users"
                      />
                    </div>
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
                    data-testid="filter-status"
                  >
                    <option value="all">All Users</option>
                    <option value="subscribers">Subscribers Only</option>
                    <option value="trial">Trial Users</option>
                    <option value="blocked">Blocked Users</option>
                  </select>
                </div>

                {/* Users Table */}
                <div className="bg-slate-800 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">User</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">School</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Subscription</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Usage</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Last Login</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {filteredUsers.map((user, index) => (
                          <tr key={`${user.user_id}-${index}`} className="hover:bg-slate-700/50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-white font-medium">
                                  {user.name || user.surname ? `${user.name} ${user.surname}` : 'No name'}
                                </p>
                                <p className="text-slate-400 text-sm">{user.email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-300">
                              {user.school_name || 'Not set'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.subscription_status === 'active'
                                  ? 'bg-green-900/50 text-green-400'
                                  : user.subscription_status === 'trial'
                                  ? 'bg-blue-900/50 text-blue-400'
                                  : 'bg-slate-700 text-slate-400'
                              }`}>
                                {user.subscription_tier || user.subscription_status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-300">
                              <div className="text-sm">
                                <p>{user.current_month_usage}/{user.monthly_limit} plans</p>
                                <p className="text-slate-500">{user.lesson_count} total lessons</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-sm">
                              {formatDate(user.last_login)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {user.subscription_status !== 'active' && (
                                  <select
                                    onChange={(e) => e.target.value && handleActivateSubscription(user.user_id, e.target.value)}
                                    className="px-2 py-1 rounded bg-slate-700 text-white text-sm"
                                    defaultValue=""
                                  >
                                    <option value="" disabled>Activate...</option>
                                    <option value="r25_monthly">R25 Plan</option>
                                    <option value="r50_monthly">R50 Plan</option>
                                  </select>
                                )}
                                {user.subscription_status === 'active' && (
                                  <Button
                                    onClick={() => handleDeactivateSubscription(user.user_id)}
                                    size="sm"
                                    variant="outline"
                                    className="text-amber-400 border-amber-700 hover:bg-amber-900/50"
                                  >
                                    Deactivate
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleBlockUser(user.user_id, !user.is_blocked)}
                                  size="sm"
                                  variant="outline"
                                  className={user.is_blocked 
                                    ? "text-green-400 border-green-700 hover:bg-green-900/50"
                                    : "text-red-400 border-red-700 hover:bg-red-900/50"
                                  }
                                >
                                  {user.is_blocked ? 'Unblock' : 'Block'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      No users found
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="space-y-4">
                {requests.length === 0 ? (
                  <div className="bg-slate-800 rounded-xl p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">All caught up!</h3>
                    <p className="text-slate-400">No pending subscription requests</p>
                  </div>
                ) : (
                  requests.map(request => (
                    <div key={request.user_id} className="bg-slate-800 rounded-xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{request.name || request.email}</p>
                          <p className="text-slate-400 text-sm">{request.email}</p>
                          <p className="text-slate-500 text-sm">{request.school_name}</p>
                          <div className="mt-2">
                            <span className="px-3 py-1 bg-teal-900/50 text-teal-400 rounded-full text-sm">
                              Requested: {request.requested_tier}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleActivateSubscription(request.user_id, request.requested_tier)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Discount Coupons</h3>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                    Create Coupon
                  </Button>
                </div>
                
                {coupons.length === 0 ? (
                  <div className="bg-slate-800 rounded-xl p-12 text-center">
                    <DollarSign className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Coupons</h3>
                    <p className="text-slate-400">Create your first discount coupon</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {coupons.map(coupon => (
                      <div key={coupon.coupon_id} className="bg-slate-800 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-mono text-lg">{coupon.code}</p>
                            <p className="text-slate-400 text-sm">
                              {coupon.discount_type === 'percentage' 
                                ? `${coupon.discount_value}% off`
                                : `R${coupon.discount_value} off`
                              }
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            coupon.status === 'active'
                              ? 'bg-green-900/50 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            {coupon.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Emails Tab */}
            {activeTab === 'emails' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Email Communication</h3>
                  <Button
                    onClick={() => {
                      setEmailData({ subject: '', message: '', to_emails: [], send_to_all: true });
                      setShowEmailComposer(true);
                    }}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" /> Compose Email
                  </Button>
                </div>
                
                {emailLogs.length === 0 ? (
                  <div className="bg-slate-800 rounded-xl p-12 text-center">
                    <Mail className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Emails Sent</h3>
                    <p className="text-slate-400">Send your first email to users</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {emailLogs.map(log => (
                      <div key={log.log_id} className="bg-slate-800 rounded-xl p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{log.subject}</p>
                            <p className="text-slate-400 text-sm mt-1 line-clamp-2">{log.message}</p>
                            <div className="flex gap-4 mt-3 text-sm">
                              <span className="text-slate-500">
                                {log.send_to_all ? 'Sent to all users' : `${log.recipients_count} recipients`}
                              </span>
                              <span className="text-green-400">{log.sent_count} delivered</span>
                              {log.failed_count > 0 && (
                                <span className="text-red-400">{log.failed_count} failed</span>
                              )}
                            </div>
                          </div>
                          <span className="text-slate-500 text-sm">
                            {new Date(log.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Composer Modal */}
      {showEmailComposer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-8 max-w-2xl w-full">
            <h3 className="text-2xl font-bold text-white mb-6">Compose Email</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-slate-300">
                  <input
                    type="checkbox"
                    checked={emailData.send_to_all}
                    onChange={(e) => setEmailData(prev => ({ ...prev, send_to_all: e.target.checked, to_emails: [] }))}
                    className="rounded"
                  />
                  Send to all teachers
                </label>
              </div>
              
              {!emailData.send_to_all && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Recipients</label>
                  <Input
                    value={emailData.to_emails.join(', ')}
                    onChange={(e) => setEmailData(prev => ({ ...prev, to_emails: e.target.value.split(',').map(s => s.trim()).filter(s => s) }))}
                    placeholder="Enter email addresses separated by commas"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Subject</label>
                <Input
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Type your message here..."
                  className="w-full px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white min-h-[200px]"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => setShowEmailComposer(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-900/30 border-blue-800 text-blue-400',
    green: 'bg-green-900/30 border-green-800 text-green-400',
    amber: 'bg-amber-900/30 border-amber-800 text-amber-400',
    teal: 'bg-teal-900/30 border-teal-800 text-teal-400',
    purple: 'bg-purple-900/30 border-purple-800 text-purple-400',
    pink: 'bg-pink-900/30 border-pink-800 text-pink-400',
    indigo: 'bg-indigo-900/30 border-indigo-800 text-indigo-400'
  };

  return (
    <div className={`rounded-xl border p-6 ${colors[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-sm opacity-80">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}
